/**
 * AportacionesIndividualV2 — Historial de aportaciones para usuarios individuales (B2C).
 *
 * Reutiliza SOLO la capa de datos del flujo de autónomo/empleo
 * (getCalculatorsUserAportations + classifyContribution/isRescate), pero la
 * presentación está construida con el lenguaje visual de individual
 * (scope .individual-v2, surface-card, MetricCard, btn-arca-primary).
 *
 * NO reutiliza ConfigurarBeneficioV2 ni src/components/contributions/* (lenguaje
 * visual de autónomo/admin). El individual solo tiene aportaciones de participante
 * (no de empresa); por eso el desglose se centra en "lo que has aportado tú".
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
	CheckCircle2,
	ChevronDown,
	Clock,
	Loader2,
	PiggyBank,
	TrendingUp,
	Wallet
} from 'lucide-react';

import { ArcaContext } from '../../../context/ArcaContext.context';
import useWithdrawalRequests from '../../../hooks/useWithdrawalRequests';
import useRescuesEnabled from '../../../hooks/useRescuesEnabled';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { MetricCard } from 'components/ui/metric-card';
import SolicitarRescatePorMotivoDialogV2 from '../../../components/SolicitarRescatePorMotivoDialogV2';

import getCalculatorsUserAportations, {
	PaginatedMonthContributions
} from '../../../api/calculators/getCalculatorsUserAportations';
import { UserAdhesionDetailsI } from '../../../api/users/interfaces/UserAdhesionDetailsI';
import getAdhesionDetails, { AdhesionMode } from '../../../api/users/getAdhesionDetails';
import getCurrentUser from '../../../api/users/getCurrentUser';
import { UserPlanI } from '../../../api/users/getPlans';
import {
	isPersonalPlanType,
	resolvePersonalAdhesionMode
} from '../../../utils/resolvePersonalAdhesionMode';

import { GenericPaginationOutput } from '../../../api/generic-dtos/genericPaginationOutput';
import {
	ContributionFromEnum,
	ContributionStatusEnum
} from '../../../api/users/interfaces/ContributionData';
import {
	classifyContribution,
	ContributionCategoryEnum,
	isRescate,
	sumRescates
} from '../../../utils/contributionClassification';
import {
	calculateTotalSaved,
	sumAdhesionDetailsField
} from '../../../utils/pensionPlanCalculations';
import { getPlanProductCodes } from '../../../utils/getPlanProductCodes';
import { fmtEur, formatArcaNumber } from '../../../utils/formatArcaNumbers';
import {
	RESCUE_REASONS,
	reasonStopsContributions,
	RescueRequestStatus
} from '../../../utils/rescueDocsCatalog';

/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — prueba de concepto
 * de rescate para planes INDIVIDUALES (sin empresa, sin relación laboral),
 * exploratoria: ver si el mismo modelo de empleo (`MiBeneficioV2.tsx`,
 * `ConfigurarBeneficioV2.tsx`) traslada a un cliente 100% individual antes de
 * decidir si entra en el MVP — caso real de referencia: un cliente que se dio
 * de alta directamente (sin empresa de por medio), no reproducido aquí por
 * nombre ni NIF. Usuaria de prueba 100% ficticia, equivalente de María pero
 * en individual: `laura.rescate.qa@example.com` (`is_individual: true`, sin
 * `company_id` ni campos de empleo, con un rescate real ya ejecutado —
 * `CANCELLMENT_PARTICIPANT_SAVINGS` por Jubilación — ver seed
 * `2026-07-28-seed-test-user-individual-seniority.sql` en `comparsa/scripts/`).
 * El bloque "aportar → rescatar" en sí vive en `IndividualDashboardV2.tsx`
 * (`isRetiredBeneficiaryIndividual`), que es el equivalente de "Mi beneficio"
 * para individual; esta pantalla (historial de aportaciones) la ve tanto un
 * activo como un beneficiario/a, así que la card informativa de rescates
 * (ver `showInfoCard` más abajo) solo se sustituye por "Rescatado (año)"
 * cuando el motivo activo SÍ corta aportaciones y ya hay un rescate
 * confirmado ese año (caso Laura) — quien rescata por un motivo que NO
 * corta aportaciones (Antigüedad, Enfermedad grave) mantiene siempre la
 * card informativa, igual que si nunca hubiera rescatado.
 *
 * `INDIVIDUAL_SENIORITY_AMOUNTS` queda vacío a propósito: ningún test persona
 * individual hoy está en el estado "tiene dinero rescatable por antigüedad
 * pero nunca ha rescatado" (el equivalente individual de Ricardo en empleo).
 * Sonia (antes usuaria de EMPLEO, `TEST_EMPLOYEE_FINANCIALS` en
 * `MiBeneficioV2.tsx`) se convirtió a 100% individual (cuenta existente, no
 * un alta nueva — ver seed de conversión en `comparsa/scripts/`), pero SIN
 * override aquí a propósito: tiene que verse como una usuaria individual
 * normal, sin rescate, con sus aportaciones reales tal cual están en
 * producción — no un importe inventado. Si en el futuro hace falta un
 * "equivalente de Ricardo" en individual, mismo patrón que
 * `RICARDO_SENIORITY_AMOUNT`: no hay cálculo real de antigüedad por fecha de
 * aportación, así que el importe se fijaría a mano para el email
 * correspondiente.
 *
 * El motivo de rescate se guarda con una clave namespaced (`rescueStorageKey`)
 * en vez de la clave plana por email que usa el plan de empleo: el motivo es
 * un estado POR PLAN, no por usuario — si en el futuro un usuario real fuera
 * "mixto" (empleo + individual con el mismo email, ver
 * `User.canActAsIndividual()` en comparsa), no debería compartir el mismo
 * motivo/estado entre ambos planes.
 */
const rescueStorageKey = (email: string) => `${email}::individual`;

// Traemos todas las aportaciones del usuario de una vez (sin filtro de año) y
// agrupamos/filtramos por año en cliente. Así el selector solo ofrece años con
// datos y cambiar de año es instantáneo (sin refetch). Límite holgado: los datos
// existen desde 2024 (sync Caser FEC_DESDE=20240101), muy por debajo de 120 meses.
const ALL_MONTHS_LIMIT = 120;

const MONTH_LABELS = [
	'Ene',
	'Feb',
	'Mar',
	'Abr',
	'May',
	'Jun',
	'Jul',
	'Ago',
	'Sep',
	'Oct',
	'Nov',
	'Dic'
];

// Formatea "YYYY-MM" → "Mmm YYYY"
const formatMonth = (yearMonth: string): string => {
	const [year, month] = yearMonth.split('-');
	return `${MONTH_LABELS[parseInt(month, 10) - 1]} ${year}`;
};

type Movement = PaginatedMonthContributions['contributions'][number];

// Traspaso REAL de entrada: dinero nuevo que entra desde una gestora externa.
// OJO: los traspasos INTERNOS (TRANSFER_INTERNAL_*) son rebalanceos del ciclo de
// vida — Caser mueve fondos DENTRO del mismo plan (p.ej. Indexa: acciones↔bonos
// para mantener el % por edad) y vienen emparejados salida/entrada por importe.
// NO son una entrada de dinero, así que NO se muestran en esta vista.
const isTraspasoEntrada = (type: ContributionFromEnum): boolean =>
	type === ContributionFromEnum.TRANSFER_EXTERNAL_INPUT;

// Movimientos visibles: aportación del partícipe, rescate y traspaso externo de
// entrada. Se excluyen los rebalanceos internos (y sus anulaciones) y las
// aportaciones de empresa (no aplican a individuales).
//
// ⚠️ LIMITACIÓN CONOCIDA / FUTURO (multi-plan):
// El endpoint agrega los movimientos de TODOS los productos del usuario (no está
// scopeado por plan). Hoy es correcto porque cada usuario tiene UN solo plan, así
// que los traspasos INTERNOS (rebalanceo de ciclo de vida o un traspaso entre dos
// planes Arca) netean a cero en el agregado → ocultarlos es lo correcto.
// El día que permitamos VARIOS planes por usuario, esta vista debería dividirse
// POR PLAN: entonces un traspaso interno plan-A→plan-B SÍ es una salida/entrada
// real de cada plan y habría que mostrarlo. Además habría que poder distinguir un
// rebalanceo de ciclo de vida de un traspaso deliberado entre planes (hoy ambos
// son TRANSFER_INTERNAL_* y no se diferencian sin el contexto de producto/CPSI).
//
// Guard multi-plan (mismo invariante que IndividualDashboardV2): el endpoint NO está
// scopeado por plan. Para usuarios con varios planes (p. ej. plan de empleo con
// aportación propia + este plan individual aún sin aportaciones), las PARTICIPANT que
// trae el endpoint son del OTRO plan. Cuando la fuente fiable por-plan (adhesionDetails)
// dice que este plan no tiene aportaciones propias, NO mostramos las PARTICIPANT (sí los
// traspasos/rescates, que sí son de este plan). `showParticipant=true` por defecto: solo
// se ocultan cuando hay plan activo confirmado con 0 aportaciones propias.
/**
 * Una aportación ya LIQUIDADA: ni pendiente de que Caser la confirme, ni fallida.
 *
 * Ojo al sentido, que el nombre y la doble negación se prestan: devuelve `true`
 * para las que están cerradas. Las PENDING y las ERROR devuelven `false`.
 */
const isSettledContribution = (c: Movement): boolean =>
	c.statusContribution !== 'PENDING' && c.statusContribution !== 'ERROR';

const isMovimientoVisible = (c: Movement, showParticipant: boolean): boolean => {
	const cat = classifyContribution(c.typeContribution);
	if (cat === ContributionCategoryEnum.PARTICIPANT) {
		// El guard multi-plan se apoya en las aportaciones propias que CONFIRMA
		// Caser en la adhesión. Una aportación en proceso (o fallida) no puede
		// estar en ese recuento todavía, así que ocultarla dejaba al usuario en
		// "Aún no hay aportaciones" justo después de acabar de hacer una.
		return showParticipant || !isSettledContribution(c);
	}
	return cat === ContributionCategoryEnum.RESCATE || isTraspasoEntrada(c.typeContribution);
};

/**
 * Suma de lo aportado por el partícipe. Las aportaciones en ERROR se excluyen:
 * ese dinero NUNCA llegó al plan, y este total es el que el usuario usa para
 * saber cuánto le queda de tope fiscal. Las PENDING sí cuentan — el dinero ya ha
 * salido de su cuenta, solo falta que Caser lo confirme.
 */
const participantSum = (movements: Movement[]): number =>
	movements
		.filter(
			(c) =>
				classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT &&
				c.statusContribution !== 'ERROR'
		)
		.reduce((sum, c) => sum + (c.contributionAmount || 0), 0);

const transferInSum = (movements: Movement[]): number =>
	movements
		.filter((c) => isTraspasoEntrada(c.typeContribution))
		.reduce((sum, c) => sum + (c.contributionAmount || 0), 0);

export interface AportacionesIndividualV2Props {
	/**
	 * Plan elegido en el selector (`user_epp_config.id`). Con dos planes del mismo
	 * tipo es lo ÚNICO que los distingue: sin él, el backend resuelve por orden y
	 * la pantalla enseña los movimientos del plan más antiguo.
	 */
	selectedPersonalPlanId?: string | null;
	/** Tipo del plan elegido: es el `mode` con el que se piden sus datos. */
	selectedPlanType?: UserPlanI['type'];
	/**
	 * Productos Caser del plan elegido, de `GET /users/plans`. Acotan sus
	 * movimientos sin depender de Caser en vivo: si se sacaran solo de la
	 * adhesión, un fallo de Caser los dejaría vacíos y el backend devolvería los
	 * movimientos de TODOS los planes.
	 */
	selectedPlanProductCodes?: number[];
}

/** Sin plan elegido (o sin tipo), decide el flag del usuario, como siempre. */
const resolveAdhesionMode = (
	selectedPlanType: UserPlanI['type'] | undefined,
	isFreelance: boolean | undefined
): AdhesionMode =>
	resolvePersonalAdhesionMode(selectedPlanType, isFreelance ? 'FREELANCE' : 'INDIVIDUAL');

const AportacionesIndividualV2: React.FC<AportacionesIndividualV2Props> = ({
	selectedPersonalPlanId,
	selectedPlanType,
	selectedPlanProductCodes
}) => {
	// Por VALOR: releer la lista de planes trae arrays nuevos con el mismo
	// contenido, y eso no puede recargar la pantalla ni plegar lo que había abierto.
	const planProductCodesKey = (selectedPlanProductCodes ?? []).join(',');
	const planProductCodes = useMemo(
		() => selectedPlanProductCodes ?? [],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[planProductCodesKey]
	);
	// El id solo viaja con un tipo personal conocido (ver `isPersonalPlanType`).
	const scopedPlanId = isPersonalPlanType(selectedPlanType) ? selectedPersonalPlanId : null;
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, userId } = useContext(ArcaContext);

	const [isLoading, setIsLoading] = useState(true);
	const [contributions, setContributions] =
		useState<GenericPaginationOutput<PaginatedMonthContributions> | null>(null);
	// Aportaciones propias del plan en curso (céntimos), de la fuente scopeada por plan
	// (getAdhesionDetails). `null` = aún no resuelto. Alimenta el guard multi-plan.
	const [planUserContributions, setPlanUserContributions] = useState<number | null>(null);
	// La última lectura de movimientos fue acotada a ESTE plan: viajó su id y con
	// productos. Solo entonces sobra el guard multi-plan.
	const [isPlanScopedRead, setIsPlanScopedRead] = useState(false);
	// Adhesión del plan: de aquí sale el saldo real que acota el rescate.
	const [planAdhesionDetails, setPlanAdhesionDetails] = useState<UserAdhesionDetailsI[]>([]);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

	const [userEmail, setUserEmail] = useState('');

	const [showSolicitarRescateModal, setShowSolicitarRescateModal] = useState(false);
	// Estado de rescate de ESTE plan, desde backend. El hook resuelve el plan
	// por modo y distingue "hay una solicitud viva" (badge) de "hay una
	// confirmada que corta aportaciones" (la vista pivota).
	// El modo hay que conocerlo en el render, no sólo dentro de fetchData: el
	// hook resuelve el plan por modo, y un autónomo societario con modo
	// INDIVIDUAL no resuelve ninguno.
	const [isFreelanceUser, setIsFreelanceUser] = useState(false);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		(async () => {
			try {
				const userRes = await getCurrentUser(token);
				if (!cancelled && userRes?.data?.user?.isFreelance) setIsFreelanceUser(true);
			} catch {
				// Sin user se queda en INDIVIDUAL (comportamiento previo).
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token]);

	const adhesionMode = resolveAdhesionMode(selectedPlanType, isFreelanceUser);

	const {
		userEppConfigId,
		openRequest,
		activeStoppingReason,
		refresh: refreshWithdrawals
	} = useWithdrawalRequests(token, adhesionMode, scopedPlanId);
	// Misma puerta que en empleo: cerrada por defecto.
	const { enabled: rescuesEnabled } = useRescuesEnabled(token, null, userId);
	const openRescueReason = openRequest?.reason ?? null;
	const stoppingRescueReason = activeStoppingReason;
	const activeRescueStatus = openRequest ? 'pending' : null;

	/**
	 * `isCancelled` descarta una respuesta que ya no interesa.
	 *
	 * 🔴 Desde que los datos dependen del plan elegido, dos lecturas pueden estar
	 * en vuelo a la vez (el usuario cambia de plan, o la lista de planes llega
	 * después del primer render). Sin esto se aplican por orden de LLEGADA y la
	 * del plan que ya nadie mira pinta sus movimientos bajo el nombre del otro.
	 * Mismo patrón que `fetchAdhesionDetails` en IndividualDashboardV2.
	 */
	const fetchData = useCallback(
		async (isCancelled: () => boolean) => {
			setIsLoading(true);
			// Nada del plan anterior sobrevive a la nueva lectura: si esta falla o
			// viene vacía, la pantalla tiene que quedar vacía, no con datos ajenos.
			setContributions(null);
			setPlanUserContributions(null);
			setIsPlanScopedRead(false);
			setPlanAdhesionDetails([]);
			try {
				// Sin plan elegido, el modo se deriva del tipo de usuario: autónomos →
				// FREELANCE (promotor del catálogo), individuales → INDIVIDUAL. Sin el mode
				// correcto el backend devuelve adhesión vacía para autónomos societarios.
				let isFreelance: boolean | undefined;
				try {
					const userRes = await getCurrentUser(token);
					const u = userRes?.data?.user;
					isFreelance = u?.isFreelance;
					if (!isCancelled() && u?.email) setUserEmail(u.email);
				} catch {
					// Sin user degradamos a INDIVIDUAL (comportamiento previo).
				}
				// Superada mientras se leía el usuario: no se le pide a Caser la
				// adhesión de un plan que ya nadie mira.
				if (isCancelled()) return;
				const mode = resolveAdhesionMode(selectedPlanType, isFreelance);
				// Adhesión PRIMERO para conocer los CPSIs del plan y scopear las aportaciones por
				// plan (multi-cuenta). Si la adhesión falla/viene vacía → planCodes = [] → el
				// backend lee legacy (muestra todo), fallback seguro.
				const adhesionRes = await getAdhesionDetails(token, mode, scopedPlanId ?? undefined).catch(
					() => null
				);
				if (isCancelled()) return;
				// Los de la lista de planes acotan aunque Caser falle; los de la
				// adhesión —pedida por el id de ESTE plan— se suman por si el catálogo
				// se ha quedado corto, para no perder movimientos sin avisar.
				const adhesionCodes = getPlanProductCodes(adhesionRes?.data ?? null);
				// Los de la lista solo valen con el plan identificado: sin tipo, la
				// adhesión se pide por modo y puede ser de OTRO plan, y mezclar sus
				// productos con los de la lista juntaría dos planes.
				const listCodes = scopedPlanId ? planProductCodes : [];
				const planCodes = [
					...listCodes,
					...adhesionCodes.filter((code) => !listCodes.includes(code))
				];
				setPlanAdhesionDetails(Array.isArray(adhesionRes?.data) ? adhesionRes.data : []);
				// Sin filtro de año: traemos todo y agrupamos/filtramos por año en cliente.
				const contribRes = await getCalculatorsUserAportations(token, {
					page: 1,
					limit: ALL_MONTHS_LIMIT,
					productCodes: planCodes,
					...(scopedPlanId ? { userEppConfigId: scopedPlanId } : {})
				});
				if (isCancelled()) return;
				if (contribRes) {
					setContributions(contribRes);
				}
				setIsPlanScopedRead(!!scopedPlanId && planCodes.length > 0);
				// Solo fijamos el total cuando hay adhesión válida con al menos un plan; si falla o
				// viene vacía, dejamos `null` → el guard no oculta nada (degrada a mostrar todo).
				if (adhesionRes && Array.isArray(adhesionRes.data) && adhesionRes.data.length > 0) {
					setPlanUserContributions(sumAdhesionDetailsField(adhesionRes.data, 'userContributions'));
				}
			} catch (error) {
				console.error('Error fetching individual contributions:', error);
			} finally {
				// También el spinner: apagarlo desde una respuesta caducada daría por
				// buena una carga que sigue en curso.
				if (!isCancelled()) setIsLoading(false);
			}
		},
		[token, scopedPlanId, selectedPlanType, planProductCodes]
	);

	// El año y los meses abiertos son del plan: otro plan empieza en el año en
	// curso y con todo plegado. Va aparte de fetchData porque esta también se
	// relanza sin cambiar de plan (token renovado) y ahí no hay que mover nada.
	useEffect(() => {
		setSelectedYear(new Date().getFullYear());
		setExpandedRows({});
	}, [scopedPlanId]);

	useEffect(() => {
		let cancelled = false;
		fetchData(() => cancelled);
		return () => {
			cancelled = true;
		};
	}, [fetchData]);

	// Guard multi-plan: ¿este plan tiene aportaciones propias? Por defecto SÍ (mostrar
	// todo). Oculta las del partícipe solo si la lectura NO va acotada y la adhesión del
	// plan confirma 0 aportaciones propias.
	// Lectura acotada (id del plan + sus productos): el backend ya devuelve solo los
	// movimientos de ESTE plan —con comparsa#824 por el id; antes, por productos—
	// y el guard solo podría ocultar aportaciones reales. Sin acotar, sigue vigente.
	const showParticipant =
		isPlanScopedRead || planUserContributions === null || planUserContributions > 0;

	// Todas las aportaciones del usuario (todos los años).
	const allMonths = useMemo(() => contributions?.items || [], [contributions]);

	// Años con movimientos visibles (de mayor a menor) — el selector solo ofrece estos.
	const availableYears = useMemo(() => {
		const years = new Set<number>();
		allMonths.forEach((m) => {
			if (m.contributions.some((c) => isMovimientoVisible(c, showParticipant))) {
				years.add(parseInt(m.month.slice(0, 4), 10));
			}
		});
		return Array.from(years).sort((a, b) => b - a);
	}, [allMonths, showParticipant]);

	// Si el año seleccionado no tiene datos, saltamos al año más reciente que sí.
	useEffect(() => {
		if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
			setSelectedYear(availableYears[0]);
		}
	}, [availableYears, selectedYear]);

	// Meses del año seleccionado con sus movimientos visibles (sin rebalanceos).
	// Se descartan los meses que se quedan sin ningún movimiento visible.
	const months = useMemo(
		() =>
			allMonths
				.filter((m) => m.month.startsWith(`${selectedYear}-`))
				.map((m) => ({
					month: m.month,
					movements: m.contributions.filter((c) => isMovimientoVisible(c, showParticipant))
				}))
				.filter((m) => m.movements.length > 0),
		[allMonths, selectedYear, showParticipant]
	);

	const userAnnual = useMemo(
		() => months.reduce((sum, m) => sum + participantSum(m.movements), 0),
		[months]
	);
	const rescatadoAnnual = useMemo(
		() => months.reduce((sum, m) => sum + Math.abs(sumRescates(m.movements)), 0),
		[months]
	);
	const movementsCount = useMemo(
		() =>
			months.reduce(
				(count, m) =>
					count +
					m.movements.filter(
						(c) => classifyContribution(c.typeContribution) === ContributionCategoryEnum.PARTICIPANT
					).length,
				0
			),
		[months]
	);

	// "Nunca ha rescatado" — misma simplificación que en ConfigurarBeneficioV2
	// (empleo): solo miramos el año seleccionado, no hay endpoint de histórico
	// completo de rescates desde aquí.
	const hasNeverRescued = rescatadoAnnual === 0;
	// UX SCAFFOLD (ago-2026) — quien rescata por un motivo que NO corta
	// aportaciones (Antigüedad, Enfermedad grave) mantiene la misma card
	// informativa que quien nunca ha rescatado, sin pasar nunca a "Rescatado
	// (año)": para ese perfil sigue teniendo sentido poder consultar/solicitar
	// más rescates. "Rescatado (año)" queda solo para quien SÍ cortó
	// aportaciones (Laura) y ya tiene un rescate confirmado ese año — a
	// diferencia de ConfigurarBeneficioV2 (empleo), aquí este componente
	// también lo ve un beneficiario/a individual (Mi beneficio no gestiona
	// Movimientos), así que la distinción por motivo hay que hacerla aquí.
	const showInfoCard =
		rescuesEnabled && (hasNeverRescued || !reasonStopsContributions(stoppingRescueReason));
	// No tenemos de Caser el importe rescatable por antigüedad de nadie: se deja
	// a 0 y el modal oculta el slider para ese motivo, en vez de inventarlo.
	const seniorityAmount = 0;
	// Disponible para rescatar: el saldo REAL del plan. Antes salía del catálogo
	// de antigüedad, vacío a propósito, así que toda solicitud se registraba por
	// el mínimo de 100 € bajo un "Disponible: 0,00 €".
	const availableToRescue = calculateTotalSaved(
		planAdhesionDetails,
		sumAdhesionDetailsField(planAdhesionDetails, 'vestedRights')
	);

	const handleYearChange = (year: number) => {
		setSelectedYear(year);
		setExpandedRows({});
	};

	const toggleRow = (monthKey: string) => {
		setExpandedRows((prev) => ({ ...prev, [monthKey]: !prev[monthKey] }));
	};

	return (
		<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
			<div className="max-w-6xl mx-auto space-y-8 p-4 pt-12 lg:p-6 lg:pt-14">
				{/* Header + selector de año. El año controla TODA la pantalla (métricas,
				    gráfica e historial), por eso vive arriba del todo. */}
				<motion.div
					initial={{ opacity: 0, y: -8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4 }}
					className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
				>
					<div className="min-w-0">
						<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
							{translate('individual.contributions.title')}
						</h1>
						<p className="text-base text-muted-foreground mt-1">
							{translate('individual.contributions.subtitle')}
						</p>
					</div>
					{availableYears.length > 1 && (
						<div className="flex items-center gap-1 bg-muted/50 rounded-full p-0.5 w-fit shrink-0">
							{availableYears.map((y) => (
								<button
									key={y}
									onClick={() => handleYearChange(y)}
									className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
										y === selectedYear
											? 'bg-primary text-primary-foreground shadow-xs'
											: 'text-muted-foreground hover:text-foreground'
									}`}
								>
									{y}
								</button>
							))}
						</div>
					)}
				</motion.div>

				{isLoading ? (
					<div className="flex items-center justify-center h-64">
						<Loader2 className="w-8 h-8 animate-spin text-primary" />
					</div>
				) : (
					<>
						{/* Métricas del año */}
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							<MetricCard
								index={0}
								label={`${translate('individual.contributions.contributedIn')} ${selectedYear}`}
								value={fmtEur(userAnnual)}
								icon={Wallet}
							/>
							<MetricCard
								index={1}
								label={translate('individual.contributions.registered')}
								value={String(movementsCount)}
								icon={PiggyBank}
							/>
							{/* Info card en vez de "Rescatado (año)": ver showInfoCard arriba. */}
							{showInfoCard ? (
								<MetricCard
									index={2}
									label={translate('rescue.aportacionesIndividual.infoLabel')}
									value={translate('rescue.aportacionesIndividual.infoValue')}
									valueClassName="text-lg"
									sub={translate('rescue.aportacionesIndividual.infoSub')}
									icon={PiggyBank}
								>
									{/* Sólo aparece con una solicitud VIVA, que por definición está
									    pendiente: una confirmada que corta aportaciones ya habría
									    pivotado la vista, y las resueltas de otra forma dejan el
									    botón libre otra vez. */}
									{openRescueReason ? (
										<Badge
											variant="outline"
											className="w-full mt-3 justify-center py-1.5 gap-1 bg-amber-500/10 text-amber-600 border-amber-500/20"
										>
											<Clock className="w-3.5 h-3.5" />
											{translate('rescue.aportacionesIndividual.statusPending')}
										</Badge>
									) : (
										<Button
											size="sm"
											onClick={() => setShowSolicitarRescateModal(true)}
											className="w-full mt-3 gap-1.5"
										>
											{translate('rescue.aportacionesIndividual.requestButton')}
										</Button>
									)}
								</MetricCard>
							) : (
								<MetricCard
									index={2}
									label={`${translate('individual.contributions.rescued')} ${selectedYear}`}
									value={fmtEur(rescatadoAnnual)}
									valueClassName="text-secondary"
									icon={TrendingUp}
								/>
							)}
						</div>
						{showInfoCard && openRescueReason && (
							<p className="text-xs text-muted-foreground -mt-4">
								{translate('rescue.aportacionesIndividual.reasonPrefix')}{' '}
								{(() => {
									const found = RESCUE_REASONS.find((r) => r.value === openRescueReason);
									return found ? translate(found.labelKey) : openRescueReason;
								})()}
							</p>
						)}

						{/* Cabecera del historial (el selector de año vive arriba) */}
						<div>
							<h2 className="font-heading font-semibold text-lg">
								{translate('individual.contributions.historyTitle')}
							</h2>
						</div>

						{/* Historial mensual */}
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
							className="space-y-3"
						>
							{months.length === 0 ? (
								<div className="surface-card text-center py-12 space-y-3">
									<PiggyBank className="w-6 h-6 mx-auto text-gray-300" />
									<p className="text-muted-foreground max-w-sm mx-auto">
										{translate('individual.contributions.empty')}
									</p>
								</div>
							) : (
								months.map((monthData) => {
									const monthKey = monthData.month;
									const isExpanded = !!expandedRows[monthKey];
									const participantTotal = participantSum(monthData.movements);
									const traspasoIn = transferInSum(monthData.movements);
									const rescateOut = Math.abs(sumRescates(monthData.movements));
									const hasDetails = monthData.movements.length > 1;
									// Si algún movimiento visible del mes sigue en trámite, se marca
									// el mes entero como pendiente (mismo criterio que la tabla de
									// movimientos de empleo, ConfigurarBeneficioV2.tsx).
									const hasPending = monthData.movements.some(
										(c) => c.statusContribution === 'PENDING'
									);

									return (
										<div key={monthKey} className="surface-card overflow-hidden">
											<button
												type="button"
												onClick={() => hasDetails && toggleRow(monthKey)}
												className={`w-full flex items-center justify-between gap-4 p-5 text-left ${
													hasDetails ? 'cursor-pointer hover:bg-muted/20' : 'cursor-default'
												} transition-colors`}
											>
												<div className="flex items-center gap-3 min-w-0">
													{hasDetails && (
														<ChevronDown
															className={`w-4 h-4 text-primary shrink-0 transition-transform duration-200 ${
																isExpanded ? 'rotate-180' : ''
															}`}
														/>
													)}
													<span className="font-semibold text-foreground">
														{formatMonth(monthKey)}
													</span>
													{hasPending && (
														<Badge
															variant="outline"
															className="gap-1 bg-amber-500/10 text-amber-600 border-amber-500/20"
														>
															<Clock className="w-3 h-3" />
															{translate('rescue.aportacionesIndividual.statusPending')}
														</Badge>
													)}
												</div>
												<div className="text-right shrink-0">
													<p className="text-lg font-bold font-heading text-foreground">
														{fmtEur(participantTotal)}
													</p>
													{traspasoIn > 0 && (
														<p className="text-xs font-medium text-primary">
															{translate('individual.contributions.transferred')}: +
															{fmtEur(traspasoIn)}
														</p>
													)}
													{rescateOut > 0 && (
														<p className="text-xs font-medium text-red-500">
															{translate('individual.contributions.rescued')}: {fmtEur(-rescateOut)}
														</p>
													)}
												</div>
											</button>

											{isExpanded && (
												<div className="border-t border-border divide-y divide-border/60">
													{monthData.movements.map((c) => {
														const rescate = isRescate(c.typeContribution);
														const transferIn = isTraspasoEntrada(c.typeContribution);
														return (
															<div
																key={c.id}
																className="flex items-center justify-between gap-4 px-5 py-3 text-sm bg-muted/10"
															>
																<span className="flex items-center gap-2 text-muted-foreground">
																	<span>
																		{new Date(c.dateContribution).toLocaleDateString('es-ES', {
																			day: 'numeric',
																			month: 'short'
																		})}
																		{c.sharesBought
																			? ` · ${formatArcaNumber(c.sharesBought)} part.`
																			: ''}
																	</span>
																	{c.statusContribution === ContributionStatusEnum.PENDING && (
																		<span
																			data-testid="contribution-status-pending"
																			className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
																		>
																			{translate('individual.contributions.status.pending') ||
																				'En proceso'}
																		</span>
																	)}
																	{c.statusContribution === ContributionStatusEnum.ERROR && (
																		<span
																			data-testid="contribution-status-error"
																			className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-600"
																		>
																			{translate('individual.contributions.status.error') ||
																				'Error'}
																		</span>
																	)}
																</span>
																<span
																	className={`font-medium ${
																		rescate
																			? 'text-red-500'
																			: transferIn
																				? 'text-primary'
																				: 'text-foreground'
																	}`}
																>
																	{rescate
																		? fmtEur(-Math.abs(c.contributionAmount))
																		: transferIn
																			? `+${fmtEur(c.contributionAmount)}`
																			: fmtEur(c.contributionAmount)}
																</span>
															</div>
														);
													})}
												</div>
											)}
										</div>
									);
								})
							)}
						</motion.div>
					</>
				)}
			</div>
			<SolicitarRescatePorMotivoDialogV2
				isOpen={showSolicitarRescateModal}
				onClose={() => setShowSolicitarRescateModal(false)}
				userEmail={userEmail}
				availableAmount={availableToRescue}
				currentReason={openRescueReason}
				userEppConfigId={userEppConfigId}
				token={token}
				isIndividual
				onSubmitted={() => refreshWithdrawals()}
			/>
		</div>
	);
};

export default AportacionesIndividualV2;
